import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import BrandLogo from "../../components/BrandLogo/BrandLogo";
import * as supplierService from "../../services/supplierService";
import { getRole } from "../../components/Apicall";
import { isSupplierRole } from "../../constants/roles";
import { FormFields, PrimaryButton, SecondaryButton, cardStyle, cleanValues } from "../../components/ui";

// The page behind the link in the invitation email (no login needed). A new supplier sets up the company and
// the first user here; one that already has a supplier login signs in and accepts inside the portal.
const shell = { minHeight: "100vh", background: "#f2f5fa", display: "flex", justifyContent: "center", padding: "32px 16px" };
const box = { ...cardStyle, padding: 28, width: "100%", maxWidth: 560, alignSelf: "flex-start" };

export default function SupplierInviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading" }); // loading | invalid | ready | done
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const signedInSupplier = (() => {
    try {
      return !!localStorage.getItem("token") && isSupplierRole(getRole());
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    let alive = true;
    supplierService.describeInvite(token).then((res) => {
      if (!alive) return;
      if (!res?.success) return setState({ status: "invalid", message: res?.message });
      const d = res.data;
      setValues({ first_name: d.contact_name || "", phone: d.phone || "", company_name: d.suggested.company_name || d.supplier_name || "", tax_id: d.suggested.tax_id || "", country: "", address: d.suggested.address || "" });
      setState({ status: "ready", invite: d });
    });
    return () => {
      alive = false;
    };
  }, [token]);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await supplierService.acceptInvite(token, cleanValues(values));
      if (res?.success) setState((s) => ({ ...s, status: "done" }));
      else toast.error(res?.message || "The account could not be created");
    } finally {
      setSaving(false);
    }
  };

  // A supplier who is already signed in accepts straight from the link.
  const acceptSignedIn = async () => {
    setSaving(true);
    try {
      const res = await supplierService.acceptInvitation({ token });
      if (res?.success) {
        toast.success(res.message || "You are now linked to this buyer");
        navigate("/supplier/buyers");
      } else toast.error(res?.message || "The invitation could not be accepted");
    } finally {
      setSaving(false);
    }
  };

  const { status, invite } = state;
  return (
    <div style={shell}>
      <div style={box}>
        <div className="mb-3">
          <BrandLogo height={34} />
        </div>
        {status === "loading" ? <div style={{ color: "#6B7280" }}>Checking your invitation…</div> : null}

        {status === "invalid" ? (
          <>
            <h5>This invitation cannot be used</h5>
            <p style={{ fontSize: 14, color: "#4B5563" }}>{state.message || "The link is not valid or has expired. Ask the buyer to send it again."}</p>
            <Link to="/login">Go to sign in</Link>
          </>
        ) : null}

        {status === "ready" && invite.existing_login ? (
          <>
            <h5>{invite.buyer_name} invited you to quote</h5>
            <p style={{ fontSize: 14, color: "#4B5563" }}>You already have a supplier login. {signedInSupplier ? "Accept the invitation to link your company to this buyer." : "Sign in with it and the invitation appears on your Home page, or use this link again while signed in."}</p>
            {signedInSupplier ? (
              <PrimaryButton onClick={acceptSignedIn} disabled={saving}>
                Accept invitation
              </PrimaryButton>
            ) : (
              <PrimaryButton onClick={() => navigate("/login")}>Sign in</PrimaryButton>
            )}
          </>
        ) : null}

        {status === "ready" && !invite.existing_login ? (
          <>
            <h5>{invite.buyer_name} invited you to quote</h5>
            <p style={{ fontSize: 14, color: "#4B5563" }}>
              Set up your supplier account in a minute. You will sign in with <strong>{invite.email}</strong> or your mobile number: a one-time code is sent, so there is no password.
            </p>
            <FormFields
              idPrefix="inv-"
              values={values}
              onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
              fields={[
                { key: "first_name", label: "Your name", required: true, max: 100 },
                { key: "phone", label: "Your mobile", type: "mobile", required: true, hint: "Used to sign in. Include the country code." },
                { key: "company_name", label: "Company name", required: true, max: 200 },
                { key: "registration_no", label: "Registration number", max: 64 },
                { key: "tax_id", label: "Tax ID (TRN / GST)", max: 64 },
                { key: "country", label: "Country", max: 100 },
                { key: "address", label: "Address", type: "textarea", max: 500 },
              ]}
            />
            <PrimaryButton onClick={submit} disabled={saving}>
              Create my account
            </PrimaryButton>
          </>
        ) : null}

        {status === "done" ? (
          <>
            <h5>Your supplier account is ready</h5>
            <p style={{ fontSize: 14, color: "#4B5563" }}>
              Sign in with <strong>{invite.email}</strong> or the mobile number you entered. You will receive a one-time code. After signing in, upload the documents {invite.buyer_name} asks for under Company → Documents.
            </p>
            <SecondaryButton onClick={() => navigate("/login")}>Go to sign in</SecondaryButton>
          </>
        ) : null}
      </div>
    </div>
  );
}
